import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RoleBadge } from "@/components/badges";
import { Check, Info, Loader2, Pencil, Plus, Send, Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useConfirm } from "@/components/ConfirmDialog";

const EMPTY_FORM = { title: "", description: "", weight: "", justification: "", planned_date: "", party_id: "", info_needed_from: [] };

function StatusBadge({ item }) {
  const { t } = useTranslation();
  const s = item.status;
  if (s === "draft")
    return <Badge variant="outline" className="bg-slate-100 text-slate-600" data-testid={`activity-status-${item.id}`}>{item.revision_of ? t("activityPlans.statusRevisionDraft") : t("activityPlans.statusDraft")}</Badge>;
  if (s === "pending_approval") {
    const ap = item.approvals || {};
    const sides = [ap.client ? `${t("activityPlans.customer")} ✓` : `${t("activityPlans.customer")} …`, ap.pruva ? "Pruva ✓" : "Pruva …"].join(" · ");
    return (
      <span className="inline-flex flex-col gap-0.5">
        <Badge variant="outline" className="bg-amber-50 text-amber-700" data-testid={`activity-status-${item.id}`}>{item.revision_of ? t("activityPlans.statusPendingRevision") : t("activityPlans.statusPending")}</Badge>
        <span className="font-mono text-[10px] text-slate-500" data-testid={`activity-side-state-${item.id}`}>{sides}</span>
      </span>
    );
  }
  if (s === "approved") return <Badge variant="outline" className="bg-emerald-50 text-emerald-700" data-testid={`activity-status-${item.id}`}>{t("badges.approved")}</Badge>;
  if (s === "rejected")
    return (
      <span className="inline-flex flex-col gap-0.5">
        <Badge variant="outline" className="bg-rose-50 text-rose-600" data-testid={`activity-status-${item.id}`}>{t("activityPlans.statusRejected")}</Badge>
        {item.rejection?.reason && <span className="max-w-64 text-[11px] text-rose-600" data-testid={`activity-rejection-${item.id}`}>{t("activityPlans.reasonPrefix")} {item.rejection.reason}</span>}
      </span>
    );
  return null;
}

export default function ActivityPlans() {
  const { askConfirm, confirmDialog } = useConfirm();
  const { selectedId } = useProject();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [rejectItem, setRejectItem] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["activity-plans", selectedId],
    queryFn: async () => (await api.get(`/projects/${selectedId}/activity-plans`)).data,
    enabled: !!selectedId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["activity-plans", selectedId] });
    qc.invalidateQueries({ queryKey: ["work-items", selectedId] });
    qc.invalidateQueries({ queryKey: ["dashboard", selectedId] });
  };
  const onErr = (e) => toast.error(fmtErr(e));

  const saveMut = useMutation({
    mutationFn: async () => {
      const body = { title: form.title, description: form.description, weight: Number(form.weight), justification: form.justification, planned_date: form.planned_date || undefined, info_needed_from: form.info_needed_from };
      if (editItem) return api.put(`/activity-plans/${editItem.id}`, body);
      return api.post(`/projects/${selectedId}/activity-plans`, { ...body, party_id: form.party_id || undefined });
    },
    onSuccess: () => {
      toast.success(editItem ? t("activityPlans.toastUpdated") : t("activityPlans.toastAdded"));
      setFormOpen(false);
      setEditItem(null);
      setForm(EMPTY_FORM);
      invalidate();
    },
    onError: onErr,
  });

  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/activity-plans/${id}`),
    onSuccess: () => { toast.success(t("activityPlans.toastDeleted")); invalidate(); },
    onError: onErr,
  });

  const reviseMut = useMutation({
    mutationFn: async (id) => api.post(`/activity-plans/${id}/revise`),
    onSuccess: () => { toast.success(t("activityPlans.toastRevisionCreated")); invalidate(); },
    onError: onErr,
  });

  const submitMut = useMutation({
    mutationFn: async (partyId) => (await api.post(`/projects/${selectedId}/activity-plans/submit`, { party_id: partyId })).data,
    onSuccess: (d) => { toast.success(t("activityPlans.toastSubmitted", { count: d.submitted })); invalidate(); },
    onError: onErr,
  });

  const approveMut = useMutation({
    mutationFn: async (id) => (await api.post(`/activity-plans/${id}/approve`)).data,
    onSuccess: (d) => { toast.success(d.fully_approved ? t("activityPlans.toastApprovedBoth") : t("activityPlans.toastApprovedOne")); invalidate(); },
    onError: onErr,
  });

  const rejectMut = useMutation({
    mutationFn: async () => api.post(`/activity-plans/${rejectItem.id}/reject`, { reason: rejectReason }),
    onSuccess: () => { toast.success(t("activityPlans.toastRejected")); setRejectItem(null); setRejectReason(""); invalidate(); },
    onError: onErr,
  });

  const groups = useMemo(() => {
    const g = {};
    (data?.items || []).filter((i) => i.status !== "superseded").forEach((it) => {
      g[it.party_id] = g[it.party_id] || { party_id: it.party_id, company: it.company_name, items: [] };
      g[it.party_id].items.push(it);
    });
    return Object.values(g).sort((a, b) => a.company.localeCompare(b.company));
  }, [data]);

  const pendingQueue = useMemo(() => {
    const mySlot = data?.is_admin ? "pruva" : "client";
    return (data?.items || []).filter((i) => i.status === "pending_approval" && !(i.approvals || {})[mySlot]);
  }, [data]);

  const openEdit = (it) => {
    setEditItem(it);
    setForm({ title: it.title, description: it.description || "", weight: it.weight, justification: it.justification || "", planned_date: it.planned_date || "", party_id: it.party_id, info_needed_from: it.info_needed_from || [] });
    setFormOpen(true);
  };

  const toggleTag = (pid) => {
    setForm((f) => ({ ...f, info_needed_from: f.info_needed_from.includes(pid) ? f.info_needed_from.filter((x) => x !== pid) : [...f.info_needed_from, pid] }));
  };

  const tagChoices = useMemo(() => {
    if (!data) return [];
    const own = editItem ? editItem.party_id : form.party_id || data.my_party_ids[0];
    return data.parties.filter((p) => p.id !== own);
  }, [data, editItem, form.party_id]);

  const renderActions = (it) => (
    <div className="flex justify-end gap-1">
      {data.can_approve && it.status === "pending_approval" && (
        <>
          <Button size="sm" variant="outline" className="text-emerald-700" onClick={() => approveMut.mutate(it.id)} disabled={approveMut.isPending} data-testid={`activity-approve-${it.id}`}>
            <Check className="mr-1 h-3.5 w-3.5" /> {t("activityPlans.approve")}
          </Button>
          <Button size="sm" variant="outline" className="text-rose-600" onClick={() => { setRejectItem(it); setRejectReason(""); }} data-testid={`activity-reject-${it.id}`}>
            <X className="mr-1 h-3.5 w-3.5" /> {t("activityPlans.reject")}
          </Button>
        </>
      )}
      {it.can_edit && (it.status === "draft" || it.status === "rejected") && (
        <>
          <Button variant="ghost" size="icon" title={t("activityPlans.editTitle", { title: "" }).replace(/ — $/, "")} onClick={() => openEdit(it)} data-testid={`activity-edit-${it.id}`}><Pencil className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" title={t("common.delete")} className="text-rose-600" onClick={() => askConfirm(t("activityPlans.deleteConfirm", { title: it.title }), () => delMut.mutate(it.id))} data-testid={`activity-delete-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
        </>
      )}
      {it.can_edit && it.status === "approved" && !it.revision_of && (
        <Button size="sm" variant="outline" onClick={() => reviseMut.mutate(it.id)} disabled={reviseMut.isPending} data-testid={`activity-revise-${it.id}`}>
          <Pencil className="mr-1 h-3.5 w-3.5" /> {t("activityPlans.revise")}
        </Button>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="activity-plans">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2.5 text-sm text-sky-900" data-testid="activity-plans-note">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          {t("activityPlans.note")}
        </div>
        <Button onClick={() => { setEditItem(null); setForm(EMPTY_FORM); setFormOpen(true); }} className="bg-slate-900 hover:bg-slate-800" data-testid="add-activity-button">
          <Plus className="mr-2 h-4 w-4" /> {t("activityPlans.addActivity")}
        </Button>
      </div>

      {data?.can_approve && pendingQueue.length > 0 && (
        <Card className="border-amber-200" data-testid="approval-queue">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t("activityPlans.approvalQueue", { count: pendingQueue.length })}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 p-4 pt-0">
            {pendingQueue.map((it) => (
              <div key={it.id} className="flex flex-wrap items-center gap-3 rounded-md border px-3 py-2 text-sm" data-testid={`queue-item-${it.id}`}>
                <span className="font-medium">{it.title}</span>
                <span className="font-mono text-xs text-slate-500">{it.weight}%</span>
                <span className="text-xs text-slate-500">{it.company}{it.revision_of ? ` · ${t("activityPlans.revision")}` : ""}</span>
                <StatusBadge item={it} />
                <div className="ml-auto">{renderActions(it)}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {isLoading && <p className="text-sm text-slate-500" data-testid="activity-plans-loading">{t("common.loading")}</p>}
      {data && groups.length === 0 && (
        <Card className="border-dashed" data-testid="activity-plans-empty">
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <p className="text-sm font-medium text-slate-700">{t("activityPlans.emptyTitle")}</p>
            <p className="max-w-md text-sm text-slate-500">
              {t("activityPlans.emptyBody")}
            </p>
          </CardContent>
        </Card>
      )}

      {groups.map((g) => {
        const total = data.weight_totals[g.party_id] ?? 0;
        const hasSubmittable = g.items.some((i) => i.status === "draft" || i.status === "rejected");
        const canSubmit = hasSubmittable && Math.abs(total - 100) <= 0.01;
        const editable = g.items.some((i) => i.can_edit);
        return (
          <Card key={g.party_id} data-testid={`activity-company-${g.party_id}`}>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base">{g.company}</CardTitle>
                <div className="flex items-center gap-3">
                  <span className={`font-mono text-sm font-semibold ${Math.abs(total - 100) <= 0.01 ? "text-emerald-600" : "text-amber-600"}`} data-testid={`weight-total-${g.party_id}`}>
                    {t("activityPlans.total", { total })}
                  </span>
                  {editable && (
                    <Button size="sm" variant="outline" disabled={!canSubmit || submitMut.isPending} onClick={() => submitMut.mutate(g.party_id)} data-testid={`activity-submit-${g.party_id}`}>
                      {submitMut.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}
                      {t("activityPlans.submitForApproval")}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("progressChart.thActivity")}</TableHead>
                    <TableHead className="w-24">{t("progressChart.thWeight")}</TableHead>
                    <TableHead>{t("progressChart.thTargetDate")}</TableHead>
                    <TableHead>{t("activityPlans.thJustification")}</TableHead>
                    <TableHead>{t("activityPlans.thInfoNeeded")}</TableHead>
                    <TableHead className="w-44">{t("common.status")}</TableHead>
                    <TableHead className="text-right">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {g.items.map((it) => (
                    <TableRow key={it.id} data-testid={`activity-row-${it.id}`}>
                      <TableCell>
                        <div className="font-medium text-slate-900">{it.title}</div>
                        {it.description && <div className="max-w-80 truncate text-xs text-slate-500" title={it.description}>{it.description}</div>}
                      </TableCell>
                      <TableCell className="font-mono text-sm font-semibold">{it.weight}%</TableCell>
                      <TableCell className="font-mono text-xs">{it.planned_date || "—"}</TableCell>
                      <TableCell className="max-w-56 truncate text-xs text-slate-500" title={it.justification}>{it.justification || "—"}</TableCell>
                      <TableCell>
                        {(it.info_needed_names || []).length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {it.info_needed_names.map((n) => <Badge key={n} variant="outline" className="bg-sky-50 font-mono text-[10px] text-sky-700">{n}</Badge>)}
                          </div>
                        ) : "—"}
                      </TableCell>
                      <TableCell><StatusBadge item={it} /></TableCell>
                      <TableCell className="text-right">{renderActions(it)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        );
      })}

      <Dialog open={formOpen} onOpenChange={(o) => { setFormOpen(o); if (!o) setEditItem(null); }}>
        <DialogContent data-testid="activity-form-dialog">
          <DialogHeader><DialogTitle>{editItem ? t("activityPlans.editTitle", { title: editItem.title }) : t("activityPlans.addActivity")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {data?.is_admin && !editItem && (
              <div className="space-y-1.5">
                <Label>{t("activityPlans.companyL")}</Label>
                <Select value={form.party_id} onValueChange={(v) => setForm({ ...form, party_id: v })}>
                  <SelectTrigger data-testid="activity-party-select"><SelectValue placeholder={t("admin.selectCompany")} /></SelectTrigger>
                  <SelectContent>
                    {data.parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.company_name} <span className="ml-1 text-xs text-slate-400">({p.role})</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>{t("activityPlans.titleScopeL")}</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("activityPlans.titlePh")} data-testid="activity-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("progress.descOptional")}</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="activity-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t("activityPlans.weightL")}</Label>
                <Input type="number" min="0.01" max="100" step="0.01" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} className="font-mono" data-testid="activity-weight-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("activityPlans.targetDateL")}</Label>
                <Input type="date" value={form.planned_date} onChange={(e) => setForm({ ...form, planned_date: e.target.value })} data-testid="activity-planned-date-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("activityPlans.justificationL")}</Label>
              <Input value={form.justification} onChange={(e) => setForm({ ...form, justification: e.target.value })} placeholder={t("activityPlans.justificationPh")} data-testid="activity-justification-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("activityPlans.infoNeededL")}</Label>
              <div className="flex flex-wrap gap-3 rounded-md border p-3" data-testid="activity-info-tags">
                {tagChoices.length === 0 && <span className="text-xs text-slate-400">{t("sharing.noOtherCompanies")}</span>}
                {tagChoices.map((p) => (
                  <label key={p.id} className="flex items-center gap-1.5 text-sm">
                    <Checkbox checked={form.info_needed_from.includes(p.id)} onCheckedChange={() => toggleTag(p.id)} data-testid={`activity-tag-${p.id}`} />
                    {p.company_name}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => saveMut.mutate()} disabled={!form.title.trim() || !Number(form.weight) || !form.planned_date || saveMut.isPending || (data?.is_admin && !editItem && !form.party_id)} className="bg-slate-900 hover:bg-slate-800" data-testid="activity-save-button">
              {saveMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {editItem ? t("common.saveChanges") : t("activityPlans.addAsDraft")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectItem} onOpenChange={(o) => !o && setRejectItem(null)}>
        <DialogContent data-testid="activity-reject-dialog">
          <DialogHeader><DialogTitle>{t("activityPlans.rejectTitle", { title: rejectItem?.title })}</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label>{t("activityPlans.reasonRequiredL")}</Label>
            <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} placeholder={t("activityPlans.rejectPh")} data-testid="reject-reason-input" />
          </div>
          <DialogFooter>
            <Button onClick={() => rejectMut.mutate()} disabled={!rejectReason.trim() || rejectMut.isPending} className="bg-rose-600 hover:bg-rose-700" data-testid="reject-submit-button">
              {rejectMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("activityPlans.rejectSubmit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {confirmDialog}
    </div>
  );
}
